import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/authContext";
import { Sidebar } from "../layout/Sidebar";
import { KnockWrapper } from "../notifications/KnockProvider";
import { Toaster } from "../ui/toaster";
import { Toaster as HotToaster } from "react-hot-toast";
import { LogoutButton } from "./logoutButton";
import { Loader } from "../layout/Loader";
import { SidebarProvider } from "../ui/sidebar";
import { canAccessRoute, getAllowedRoutes } from "../../lib/permissions";

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, accessLevel, userRole, routePermissions, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check Provider access synchronously
  if (accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0) {
    const allowedRoutes = ["/", "/orders"];
    if (!allowedRoutes.includes(location.pathname)) {
      return <Navigate to="/orders" replace />;
    }
  } else if (userRole && !canAccessRoute(userRole, location.pathname, routePermissions)) {
    // Some utility routes are not in the sidebar and should inherit access from a parent route.
    // /shipment_tracker is opened from within /orders, so it should be accessible to anyone
    // who has /orders access without needing its own DB permission group.
    const inheritedRoutes: Record<string, string> = {
      "/shipment_tracker": "/orders",
    };
    const parentRoute = inheritedRoutes[location.pathname];
    if (parentRoute && canAccessRoute(userRole, parentRoute, routePermissions)) {
      // Allow access — falls through to render children below
    } else {
      // If user is at root and doesn't have dashboard access, find their first allowed route
      if (location.pathname === "/") {
        const allowed = getAllowedRoutes(userRole, routePermissions);
        if (allowed.length > 0) {
          // Redirect to the first available route
          return <Navigate to={allowed[0]} replace />;
        }
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white shadow dark:bg-gray-800 flex-shrink-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {{
                  "/": "Dashboard",
                  "/b2c_products_management": "B2C Products Management",
                  "/b2c_products_mapping": "B2C Products Mapping",
                  "/b2c_products_master": "B2C Products Master",
                  "/b2c_pending_orders": "B2C Pending Orders",
                  "/b2c_refunds": "Refund Processing",
                  "/orders": "Orders",
                }[location.pathname] || "Dashboard"}
              </h1>
              <div className="flex items-center space-x-4">
                {user && (
                  <div className="flex items-center space-x-2 px-2 py-1.5 bg-blue-50 dark:bg-blue-950/50 rounded-md border border-blue-200 dark:border-blue-800/50">
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {user.email}
                    </span>
                  </div>
                )}
                <KnockWrapper />
                <LogoutButton size={20} />
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
              <Toaster />
              <HotToaster position="top-right" toastOptions={{ duration: 4000 }} />
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
